// frontend/src/lib/identity.js
// ✅ FIXED: Never calls Pi.authenticate — only reads from localStorage
// Pi.authenticate is ONLY called once in App.js

/** Detect Pi SDK (Pi Browser only) */
export function hasPiSdk() {
  return typeof window !== "undefined" && window.Pi && window.Pi.authenticate;
}
/** Read the stored auth blob (or null). */
export function getStoredAuth() {
  try {
    const raw = localStorage.getItem("artibytes_auth_v1");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Returns true if there's a usable Pi session in localStorage. */
export function isPiAuthenticated() {
  const stored = getStoredAuth();
  return !!(stored?.accessToken && stored?.user?.uid);
}
/**
 * Sign in with Pi — reads from localStorage only.
 * App.js is responsible for calling Pi.authenticate and storing the token.
 * This function just verifies the stored token with the backend.
 */
export async function signInWithPi() {
  if (!hasPiSdk()) throw new Error("Pi SDK not available (open in Pi Browser)");

  // ✅ Read from storage — App.js already authenticated
  let stored = null;
  try {
    const raw = localStorage.getItem("artibytes_auth_v1");
    stored = raw ? JSON.parse(raw) : null;
  } catch {}

  if (!stored?.accessToken) {
    throw new Error("Not authenticated — token not found in storage");
  }

  // Hit /api/me so backend verifies token via /v2/me and upserts user
  try {
    const API_BASE = process.env.REACT_APP_API_BASE ||
      (typeof window !== "undefined" && window.__ARTIBYTES_API_BASE__
        ? String(window.__ARTIBYTES_API_BASE__)
        : "") ||
      "https://artibytes-api-191042099955.europe-west4.run.app";

    await fetch(`${API_BASE}/api/me`, {
      method: "GET",
      credentials: "omit",
      headers: getIdentityHeaders(),
    });
  } catch {}

  return stored.user;
}

/** Logout helper */
export async function signOutPi() {
  try {
    localStorage.removeItem("artibytes_auth_v1");
    localStorage.removeItem("artibytes_access_token");
    localStorage.removeItem("mobile-app-webview-ui_access-token");
  } catch {}
}

/**
 * ✅ FIXED: No longer calls Pi.authenticate
 * Token refresh is handled by App.js only
 */
export async function refreshPiToken() {
  console.log("[Identity] Token refresh is handled by App.js");
  return false;
}

/**
 * Build headers for API calls.
 */
export function getIdentityHeaders() {
  const h = {};
  let stored = null;

  try {
    const raw = localStorage.getItem("artibytes_auth_v1");
    stored = raw ? JSON.parse(raw) : null;
  } catch {}

  if (stored?.accessToken) {
    h["Authorization"] = `Bearer ${stored.accessToken}`;
    h["x-pi-access-token"] = stored.accessToken;
  }

  if (stored?.user?.uid) h["x-pi-uid"] = stored.user.uid;
  if (stored?.user?.username) h["x-pi-username"] = stored.user.username;

  return h;
}

/**
 * Back-compat shim — returns stored identity without triggering auth.
 */
export async function waitForIdentity() {
  let cachedUser = null;
  try {
    const stored = localStorage.getItem("artibytes_auth_v1");
    if (stored) cachedUser = JSON.parse(stored)?.user || null;
  } catch {}
  return { headers: getIdentityHeaders(), user: cachedUser };
}

export function initializeAuth() {
  // No-op — auth is handled by App.js
}